/**
 * Algebra Inspector Panel Component
 *
 * Displays contract algebra expressions in a tree view.
 * Supports click-to-highlight for dual-view synchronization.
 *
 * @see docs/designs/smart-asset-algebra-visualization.md
 * @see docs/discoveries/2026-01-05-ui-schema-consumption.md
 */
import { memo, useMemo, useCallback } from "react";
import type { ContractAlgebra, ContractExpression } from "../../../types/valueFlows";
import { useHighlightOptional } from "./HighlightContext";

interface AlgebraInspectorPanelProps {
  /** Contract algebra from schema */
  contractAlgebra?: ContractAlgebra;
  /** Compact mode for smaller displays */
  compact?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Status type for expression validation
 */
type ExpressionStatus = "ok" | "warn" | "error";

/**
 * Determine expression status based on validation
 */
function getExpressionStatus(
  _id: string,
  expr: ContractExpression,
  _contractAlgebra: ContractAlgebra,
): ExpressionStatus {
  // Check for missing references
  if (expr.obs_ref && !expr.obs_ref.startsWith("obs.")) {
    return "warn";
  }
  if (expr.date_ref && !expr.date_ref.startsWith("dates.")) {
    return "warn";
  }
  // More validation can be added here
  return "ok";
}

/**
 * Get operator color for visual distinction
 */
function getOpColor(op: string): string {
  switch (op) {
    case "get":
    case "truncate":
      return "text-primary";
    case "scale":
      return "text-purple-400";
    case "or":
    case "and":
      return "text-sa-warning";
    case "give":
      return "text-sa-success";
    case "anytime":
      return "text-cyan-400";
    default:
      return "text-sa-text-dim";
  }
}

/**
 * Status pill component
 */
const StatusPill = memo(({ status }: { status: ExpressionStatus }) => {
  const colors = {
    ok: "bg-sa-success/20 text-sa-success",
    warn: "bg-sa-warning/20 text-sa-warning",
    error: "bg-sa-danger/20 text-sa-danger",
  };

  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${colors[status]}`}>
      {status}
    </span>
  );
});

StatusPill.displayName = "StatusPill";

/**
 * Expression row component
 */
const ExpressionRow = memo(
  ({
    id,
    expression,
    status,
    isHighlighted,
    onClick,
    compact,
  }: {
    id: string;
    expression: ContractExpression;
    status: ExpressionStatus;
    isHighlighted: boolean;
    onClick: () => void;
    compact?: boolean;
  }) => {
    const opColor = getOpColor(expression.op);

    return (
      <button
        type="button"
        onClick={onClick}
        className={`
          w-full text-left px-2 py-1.5 rounded transition-colors
          ${isHighlighted ? "bg-primary/20 ring-1 ring-primary/50" : "hover:bg-sa-surface-hover"}
        `}
      >
        <div className="flex items-center gap-2">
          {/* Status pill */}
          {!compact && <StatusPill status={status} />}

          {/* Expression ID */}
          <code className="text-xs font-mono text-sa-text truncate flex-1">{id}</code>

          {/* Operator badge */}
          <code className={`text-xs font-mono font-semibold ${opColor}`}>{expression.op}</code>
        </div>

        {/* Expression details (non-compact) */}
        {!compact && (
          <div className="mt-1 text-[11px] text-sa-text-muted space-y-0.5">
            {/* Observable reference */}
            {expression.obs_ref && (
              <div>
                obs: <span className="text-purple-400">{expression.obs_ref}</span>
              </div>
            )}

            {/* Date reference */}
            {expression.date_ref && (
              <div>
                date: <span className="text-primary">{expression.date_ref}</span>
              </div>
            )}

            {/* Argument reference */}
            {expression.arg && (
              <div>
                arg: <span className="text-cyan-400">{expression.arg}</span>
              </div>
            )}

            {/* Multiple arguments */}
            {expression.args && expression.args.length > 0 && (
              <div>
                args: <span className="text-cyan-400">{expression.args.join(", ")}</span>
              </div>
            )}

            {/* Choice metadata for or expressions */}
            {expression.choice_metadata && (
              <div className="mt-1 pl-2 border-l border-sa-warning/30">
                <div className="text-sa-warning">
                  Branches: {expression.choice_metadata.labels.join(" | ")}
                </div>
                <div className="text-sa-text-muted">
                  Default:{" "}
                  {expression.choice_metadata.labels[expression.choice_metadata.default_branch]}
                  {" · "}
                  {expression.choice_metadata.exercise_type}
                </div>
              </div>
            )}

            {/* Graph binding */}
            {expression.graph_binding && (
              <div className="text-sa-text-dim">
                → <span className="text-cyan-400">{expression.graph_binding.node_id}</span>
                {expression.graph_binding.highlight_group && (
                  <span className="text-sa-text-muted">
                    {" "}
                    ({expression.graph_binding.highlight_group})
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </button>
    );
  },
);

ExpressionRow.displayName = "ExpressionRow";

/**
 * Algebra Inspector Panel
 *
 * Displays contract algebra expressions with validation status
 * and cross-highlighting support.
 */
export const AlgebraInspectorPanel = memo(
  ({ contractAlgebra, compact = false, className = "" }: AlgebraInspectorPanelProps) => {
    const highlightContext = useHighlightOptional();

    // Sort expressions for display (entrypoint first, then alphabetically)
    const sortedExpressions = useMemo(() => {
      if (!contractAlgebra?.expressions) return [];

      const entries = Object.entries(contractAlgebra.expressions);
      return entries.sort(([idA], [idB]) => {
        // Put "entrypoint" or "root" first
        if (idA === "entrypoint" || idA === "root") return -1;
        if (idB === "entrypoint" || idB === "root") return 1;
        return idA.localeCompare(idB);
      });
    }, [contractAlgebra?.expressions]);

    // Handle expression click for highlighting
    const handleExpressionClick = useCallback(
      (id: string, expression: ContractExpression) => {
        if (!highlightContext) return;

        const nodeId = expression.graph_binding?.node_id;
        const highlightGroup = expression.graph_binding?.highlight_group;

        // Build node IDs array
        const nodeIds: string[] = [];
        if (nodeId) {
          // Convert schema node_id to React Flow node_id format
          // e.g., "core" -> "internal_core", "multisig_gate" -> "gate_multisig_gate"
          nodeIds.push(`internal_${nodeId}`);
          nodeIds.push(`gate_${nodeId}`);
          nodeIds.push(`source_${nodeId}`);
          nodeIds.push(`sink_${nodeId}`);
          nodeIds.push(`external_${nodeId}`);
        }

        highlightContext.highlightExpressions([id], nodeIds, highlightGroup);
      },
      [highlightContext],
    );

    if (!contractAlgebra || sortedExpressions.length === 0) {
      return (
        <div className={`text-sm text-sa-text-muted p-3 ${className}`}>
          No contract algebra defined
        </div>
      );
    }

    return (
      <div className={`flex flex-col ${className}`}>
        {/* Header */}
        <div className="px-3 py-2 border-b border-sa-border">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-sa-text">Contract Algebra</h3>
            <span className="text-xs text-sa-text-muted">
              {sortedExpressions.length} expression{sortedExpressions.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* Expression list */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {sortedExpressions.map(([id, expression]) => {
            const status = getExpressionStatus(id, expression, contractAlgebra);
            const isHighlighted = highlightContext?.isExpressionHighlighted(id) ?? false;

            return (
              <ExpressionRow
                key={id}
                id={id}
                expression={expression}
                status={status}
                isHighlighted={isHighlighted}
                onClick={() => handleExpressionClick(id, expression)}
                compact={compact}
              />
            );
          })}
        </div>

        {/* Bindings summary */}
        {contractAlgebra.bindings && !compact && (
          <div className="px-3 py-2 border-t border-sa-border text-xs text-sa-text-muted">
            <div className="font-medium text-sa-text-dim mb-1">Bindings</div>
            {Object.entries(contractAlgebra.bindings).map(([category, bindings]) => (
              <div key={category} className="flex items-center gap-1">
                <span className="text-sa-text-dim">{category}:</span>
                <span className="text-sa-text-muted">{Object.keys(bindings).length} bound</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  },
);

AlgebraInspectorPanel.displayName = "AlgebraInspectorPanel";
